import { FC } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { SelectedResultItemQuestions } from './questions';
import { SelectedResultItemProps } from './types';
import { DevEnvWrapper } from '@components/common/DevEnvWrapper';
import { defineIfRequestedFinanceCase } from '../../helpers';

export const SelectedResultItem: FC<SelectedResultItemProps> = ({ resultItem, financeDetails }) => {
  const {
    productRangeKey,
    repaymentsKey,
    initialRateKey,
    maxFinancingKey,
    maxFinancingRequestedKey,
    ltvKey,
    selectedProductKey,
    typeKey,
    initialTermLabelKey,
    initialTermValueKey,
    typeValueKey,
  } = SelectedResultItemQuestions;

  const isRequestedFinanceCase = defineIfRequestedFinanceCase(
    resultItem.requestedFinance,
    financeDetails.financingAmount,
  );

  return (
    <div className={'border-grass-light border-3 relative my-5 w-full overflow-hidden rounded-2xl border-solid '}>
      <div className={'xs:flex-col flex justify-between md:flex-row'}>
        <div className={'bg-grass-light xs:w-full xs:rounded-b-2xl xs:py-5 md:w-2/6 md:rounded-none md:py-10'}>
          <p className={'font-satoshi-medium m-0 mb-2 block text-center font-medium text-white'}>
            <FormattedMessage id={selectedProductKey} />
          </p>
          <div className={'flex flex-nowrap place-content-center text-lg text-white'}>
            <p className={'font-satoshi-black xs:text-2xl md:text-1xl m-0 font-black'}>
              <FormattedMessage id={productRangeKey + resultItem.productRange} />
            </p>
          </div>
          <DevEnvWrapper>id: {resultItem.id}</DevEnvWrapper>
        </div>
        <div className={'xs:w-full grid grid-cols-3 grid-rows-2 gap-y-5 p-5 md:w-4/6'}>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={initialTermLabelKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedMessage id={initialTermValueKey} values={{ years: resultItem.term }} />
            </p>
          </div>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={typeKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedMessage id={typeValueKey + resultItem.productType} />
            </p>
          </div>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={ltvKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedNumber
                value={resultItem.ltv}
                style={'percent'}
                minimumFractionDigits={0}
                maximumFractionDigits={2}
              />
            </p>
          </div>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={repaymentsKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedNumber
                value={resultItem.repayments}
                style={'currency'}
                currency={'GBP'}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </p>
          </div>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={initialRateKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedNumber
                value={resultItem.initialRate}
                style={'percent'}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </p>
          </div>
          <div>
            <p className={'text-ash font-satoshi-medium m-0 mb-0.5 text-base'}>
              <FormattedMessage id={isRequestedFinanceCase ? maxFinancingRequestedKey : maxFinancingKey} />
            </p>
            <p className={'text-grass-light font-satoshi-black m-0 text-lg'}>
              <FormattedNumber
                value={resultItem.requestedFinance}
                style={'currency'}
                currency={'GBP'}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            </p>
          </div>
        </div>
        <DevEnvWrapper>id: {resultItem.id}</DevEnvWrapper>
      </div>
    </div>
  );
};
